// Bayesian A/B comparison for rate (Beta-Binomial) metrics.
//
// Each variant's conversion rate is modelled as Beta(successes + 1, failures + 1)
// (a uniform prior). We draw Monte-Carlo samples from both posteriors to get:
//   - P(A > B)              — share of draws where A's rate beats B's
//   - the expected difference and its 95% credible interval
//   - posterior density curves for the chart
//
// The RNG is seeded from the inputs so the same data yields the same result
// across re-renders (no flicker) without depending on Math.random.

export type BayesianResult = {
  probAB: number; // P(rate_A > rate_B) in [0, 1]
  expectedDiff: number; // E[rate_A - rate_B]
  ci: [number, number]; // 95% credible interval of (rate_A - rate_B)
  rateA: number;
  rateB: number;
  sizeA: number;
  sizeB: number;
  density: { x: number; a: number; b: number }[];
};

function mulberry32(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function hashSeed(...values: number[]): number {
  let h = 0x811c9dc5;
  for (const value of values) {
    h = Math.imul(h ^ Math.round(value), 0x01000193) >>> 0;
  }
  return h >>> 0;
}

// Lanczos approximation of ln Γ(z).
function logGamma(z: number): number {
  const c = [
    0.99999999999980993, 676.5203681218851, -1259.1392167224028, 771.32342877765313,
    -176.61502916214059, 12.507343278686905, -0.13857109526572012, 9.9843695780195716e-6,
    1.5056327351493116e-7,
  ];
  if (z < 0.5) {
    return Math.log(Math.PI / Math.sin(Math.PI * z)) - logGamma(1 - z);
  }
  const zz = z - 1;
  let x = c[0];
  for (let i = 1; i < c.length; i += 1) x += c[i] / (zz + i);
  const t = zz + c.length - 1.5;
  return 0.5 * Math.log(2 * Math.PI) + (zz + 0.5) * Math.log(t) - t + Math.log(x);
}

function betaPdf(x: number, a: number, b: number): number {
  if (x <= 0 || x >= 1) return 0;
  const logBeta = logGamma(a) + logGamma(b) - logGamma(a + b);
  return Math.exp((a - 1) * Math.log(x) + (b - 1) * Math.log(1 - x) - logBeta);
}

// Density of Gamma(shape, rate) at x.
function gammaPdf(x: number, shape: number, rate: number): number {
  if (x <= 0) return 0;
  return Math.exp(shape * Math.log(rate) + (shape - 1) * Math.log(x) - rate * x - logGamma(shape));
}

function gaussian(rng: () => number): number {
  let u = 0;
  let v = 0;
  while (u === 0) u = rng();
  while (v === 0) v = rng();
  return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
}

// Marsaglia–Tsang gamma sampler (shape k, scale 1).
function sampleGamma(k: number, rng: () => number): number {
  if (k < 1) return sampleGamma(1 + k, rng) * rng() ** (1 / k);
  const d = k - 1 / 3;
  const c = 1 / Math.sqrt(9 * d);
  for (;;) {
    let x = 0;
    let v = 0;
    do {
      x = gaussian(rng);
      v = 1 + c * x;
    } while (v <= 0);
    v *= v * v;
    const u = rng();
    if (u < 1 - 0.0331 * x * x * x * x) return d * v;
    if (Math.log(u) < 0.5 * x * x + d * (1 - v + Math.log(v))) return d * v;
  }
}

function sampleBeta(a: number, b: number, rng: () => number): number {
  const x = sampleGamma(a, rng);
  const y = sampleGamma(b, rng);
  return x / (x + y);
}

function quantile(sorted: Float64Array, q: number): number {
  if (sorted.length === 0) return 0;
  const pos = (sorted.length - 1) * q;
  const base = Math.floor(pos);
  const rest = pos - base;
  const next = sorted[base + 1];
  return next === undefined ? sorted[base] : sorted[base] + rest * (next - sorted[base]);
}

export function compareBeta(
  successesA: number,
  trialsA: number,
  successesB: number,
  trialsB: number,
  samples = 20000,
): BayesianResult {
  // Clamp so the Beta parameters stay valid even for non-binomial proxies
  // (e.g. a money "rate" where the numerator can exceed the denominator).
  const succA = Math.max(0, successesA);
  const succB = Math.max(0, successesB);
  const aA = succA + 1;
  const bA = Math.max(0, trialsA - succA) + 1;
  const aB = succB + 1;
  const bB = Math.max(0, trialsB - succB) + 1;

  const rng = mulberry32(hashSeed(successesA, trialsA, successesB, trialsB, samples));

  const diffs = new Float64Array(samples);
  let aGreater = 0;
  for (let i = 0; i < samples; i += 1) {
    const ra = sampleBeta(aA, bA, rng);
    const rb = sampleBeta(aB, bB, rng);
    diffs[i] = ra - rb;
    if (ra > rb) aGreater += 1;
  }
  diffs.sort();

  let sum = 0;
  for (let i = 0; i < diffs.length; i += 1) sum += diffs[i];

  // Density grid zoomed to where the two posteriors actually live.
  const meanA = aA / (aA + bA);
  const meanB = aB / (aB + bB);
  const sdA = Math.sqrt((aA * bA) / ((aA + bA) ** 2 * (aA + bA + 1)));
  const sdB = Math.sqrt((aB * bB) / ((aB + bB) ** 2 * (aB + bB + 1)));
  let lo = Math.max(0, Math.min(meanA - 4 * sdA, meanB - 4 * sdB));
  let hi = Math.min(1, Math.max(meanA + 4 * sdA, meanB + 4 * sdB));
  if (hi <= lo) {
    lo = 0;
    hi = 1;
  }
  const steps = 120;
  const density: { x: number; a: number; b: number }[] = [];
  for (let i = 0; i <= steps; i += 1) {
    const x = lo + ((hi - lo) * i) / steps;
    density.push({ x, a: betaPdf(x, aA, bA), b: betaPdf(x, aB, bB) });
  }

  return {
    probAB: aGreater / samples,
    expectedDiff: sum / samples,
    ci: [quantile(diffs, 0.025), quantile(diffs, 0.975)],
    rateA: trialsA > 0 ? succA / trialsA : 0,
    rateB: trialsB > 0 ? succB / trialsB : 0,
    sizeA: trialsA,
    sizeB: trialsB,
    density,
  };
}

// Gamma-Poisson comparison for a positive continuous rate (value per unit, e.g.
// money per delivery). The mean rate λ = sum / count is modelled with a
// conjugate posterior λ ~ Gamma(sum + 1, count); a tiny prior rate keeps it
// valid when count is 0.
export function compareGamma(
  sumA: number,
  countA: number,
  sumB: number,
  countB: number,
  samples = 20000,
): BayesianResult {
  const PRIOR_SHAPE = 1;
  const PRIOR_RATE = 1e-6;
  const shapeA = Math.max(0, sumA) + PRIOR_SHAPE;
  const rateParamA = Math.max(0, countA) + PRIOR_RATE;
  const shapeB = Math.max(0, sumB) + PRIOR_SHAPE;
  const rateParamB = Math.max(0, countB) + PRIOR_RATE;

  const rng = mulberry32(hashSeed(sumA, countA, sumB, countB, samples));

  const diffs = new Float64Array(samples);
  let aGreater = 0;
  for (let i = 0; i < samples; i += 1) {
    const la = sampleGamma(shapeA, rng) / rateParamA;
    const lb = sampleGamma(shapeB, rng) / rateParamB;
    diffs[i] = la - lb;
    if (la > lb) aGreater += 1;
  }
  diffs.sort();

  let sum = 0;
  for (let i = 0; i < diffs.length; i += 1) sum += diffs[i];

  const meanA = shapeA / rateParamA;
  const meanB = shapeB / rateParamB;
  const sdA = Math.sqrt(shapeA) / rateParamA;
  const sdB = Math.sqrt(shapeB) / rateParamB;
  let lo = Math.max(0, Math.min(meanA - 4 * sdA, meanB - 4 * sdB));
  let hi = Math.max(meanA + 4 * sdA, meanB + 4 * sdB);
  if (hi <= lo) {
    lo = 0;
    hi = Math.max(meanA, meanB, 1) * 2;
  }
  const steps = 120;
  const density: { x: number; a: number; b: number }[] = [];
  for (let i = 0; i <= steps; i += 1) {
    const x = lo + ((hi - lo) * i) / steps;
    density.push({ x, a: gammaPdf(x, shapeA, rateParamA), b: gammaPdf(x, shapeB, rateParamB) });
  }

  return {
    probAB: aGreater / samples,
    expectedDiff: sum / samples,
    ci: [quantile(diffs, 0.025), quantile(diffs, 0.975)],
    rateA: countA > 0 ? sumA / countA : 0,
    rateB: countB > 0 ? sumB / countB : 0,
    sizeA: countA,
    sizeB: countB,
    density,
  };
}
